import React, { Fragment, useRef, useState } from 'react';
import { Modal } from './Modal';
import { FormSelect } from '../form/FormSelect';
import { FormSwitch } from '../form/FormSwitch';
import { Button } from '../Button';
import { MutationForm } from '../form/graphql/MutationForm';
import { Icon } from '../Icon';
import { useAlert } from '../alert/AlertStack';
import classNames from 'classnames';
import './ModalImport.scss';
import { useUser } from 'components/user/UserProvider';
import { MANUFACTURING_LIST_CONFIGURATIONS, OPTION_LIST_CONFIGURATIONS } from 'helpers/optionConfiguration';
/**
 * @component ModalImport
 * @since 1.0.0
 * @hidden
 */
export function ModalImport(props) {
    const { onClose, importMutation, templateMutation, refetchQuery, refetchQueryFieldName, refetchQueryVariables, input, isEndClient, isSaleItem, itemOnly, optionOnly, manufacturing, entityId, onOptionConfigurationChange } = props;
    const [error, setError] = useState(null);
    const [importProcessing, setImportProcessing] = useState(false);
    const [file, setFile] = useState(null);
    const [downloadTemplateError, setDownloadTemplateError] = useState(null);
    const [downloadTemplateProcessing, setDownloadTemplateProcessing] = useState(false);
    const alert = useAlert();
    const user = useUser();
    const canManageVariants = user.can('saleItemVariant.manage');
    const inputFile = useRef(null);
    /**
     * @function onDownloadTemplateSubmit
     * @since 1.0.0
     * @hidden
     */
    function onDownloadTemplateSubmit(formValues) {
        const { separator } = formValues;
        setDownloadTemplateProcessing(true);
        let additionalVariables = {};
        let query = {};
        if (optionOnly) {
            additionalVariables = Object.assign(Object.assign({}, additionalVariables), { optionOnly: true });
            query = Object.assign(Object.assign({}, query), { optionConfiguration: OPTION_LIST_CONFIGURATIONS });
        }
        if (itemOnly) {
            additionalVariables = Object.assign(Object.assign({}, additionalVariables), { itemOnly: true });
            query = Object.assign(Object.assign({}, query), { optionConfiguration: ['ITEM_ONLY'] });
        }
        if (manufacturing) {
            additionalVariables = Object.assign(Object.assign({}, additionalVariables), { manufacturing: true });
            query = Object.assign(Object.assign({}, query), { optionConfiguration: MANUFACTURING_LIST_CONFIGURATIONS });
        }
        if (isEndClient) {
            additionalVariables = Object.assign(Object.assign({}, additionalVariables), { isEndClient: true });
        }
        if (isEndClient === false) {
            additionalVariables = Object.assign(Object.assign({}, additionalVariables), { isEndClient: false });
        }
        if (entityId) {
            return {
                variables: {
                    input: Object.assign(Object.assign(Object.assign({ id: entityId, separator }, additionalVariables), input), { query: query, template: true })
                }
            };
        }
        else {
            return {
                variables: {
                    input: Object.assign(Object.assign(Object.assign({ separator }, additionalVariables), input), { query: query, template: true })
                }
            };
        }
    }
    /**
     * @function onDownloadTemplateSuccess
     * @since 1.0.0
     * @hidden
     */
    function onDownloadTemplateSuccess(result) {
        let fileResult;
        try {
            //TODO : Without this delay, sometimes file is not available yet
            setTimeout(function () {
                if (result.exportClients) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportClients;
                }
                if (result.exportContacts) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportContacts;
                }
                if (result.exportSaleAgreements) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportSaleAgreements;
                }
                if (result.exportDeals) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportDeals;
                }
                if (result.exportSaleItems) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportSaleItems;
                }
                if (result.exportSaleItemVariants) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportSaleItemVariants;
                }
                if (result.exportSaleItemOptions) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportSaleItemOptions;
                }
                if (result.exportSaleItemVariantOptions) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportSaleItemVariantOptions;
                }
                if (result.exportSaleItemRelations) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportSaleItemRelations;
                }
                if (result.exportSaleItemOptionRelations) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportSaleItemOptionRelations;
                }
                if (result.exportSaleItemVariantOptionRelations) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportSaleItemVariantOptionRelations;
                }
                if (result.exportSaleItemManufacturing) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportSaleItemManufacturing;
                }
                if (result.exportPriceListItems) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportPriceListItems;
                }
                if (result.exportVouchers) {
                    fileResult = result === null || result === void 0 ? void 0 : result.exportVouchers;
                }
                download(fileResult.filepath, fileResult.filename);
                setDownloadTemplateProcessing(false);
            }, 1200);
        }
        catch (error) {
            setDownloadTemplateError(error);
            setDownloadTemplateProcessing(false);
            return;
        }
    }
    /**
     * @function onDownloadTemplateFailure
     * @since 1.0.0
     * @hidden
     */
    function onDownloadTemplateFailure(error) {
        setDownloadTemplateError(error);
        setDownloadTemplateProcessing(false);
    }
    /**
     * @function download
     * @since 1.0.0
     * @hidden
     */
    function download(dataurl, filename) {
        const link = document.createElement('a');
        link.href = dataurl;
        link.download = filename;
        link.click();
    }
    /**
     * @function onModalSubmit
     * @since 1.0.0
     * @hidden
     */
    function onModalSubmit(formValues) {
        const { separator, applyPriceUpdate } = formValues;
        setImportProcessing(true);
        const refetchOptions = refetchQuery
            ? {
                refetchQueries: [
                    { query: refetchQuery, variables: { input: refetchQueryVariables } }
                ],
                update(cache) {
                    cache.evict({ fieldName: refetchQueryFieldName });
                }
            }
            : {};
        const input = {
            file,
            separator
        };
        if (entityId) {
            input.id = entityId;
        }
        if (optionOnly) {
            input.applyPriceUpdate = Boolean(applyPriceUpdate);
        }
        return Object.assign({ variables: { input } }, refetchOptions);
    }
    /**
     * @function onSuccess
     * @since 1.0.0
     * @hidden
     */
    function onSuccess(result) {
        setImportProcessing(false);
        props.importSuccess && props.importSuccess(result);
        onClose();
        alert.present({
            title: React.createElement("p", null, t('Data has been imported successfully'))
        });
    }
    /**
     * @function onFailure
     * @since 1.0.0
     * @hidden
     */
    function onFailure(error) {
        setError(error);
        setImportProcessing(false);
        alert.present({ error });
    }
    /**
     * @function onDropFile
     * @since 1.0.0
     * @hidden
     */
    function onDropFile(e) {
        e.stopPropagation();
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        setFile(file);
    }
    /**
     * @function onDragOver
     * @since 1.0.0
     * @hidden
     */
    function onDragOver(e) {
        e.stopPropagation();
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    }
    /**
     * @function onSelectFile
     * @since 1.0.0
     * @hidden
     */
    function onSelectFile(e) {
        e.stopPropagation();
        e.preventDefault();
        const file = e.currentTarget.files && e.currentTarget.files[0];
        setFile(file);
    }
    /**
     * @function onBrowseClick
     * @since 1.0.0
     * @hidden
     */
    const onBrowseClick = () => {
        // 'current' points to the mounted file input element
        if (inputFile.current) {
            inputFile.current.click();
        }
    };
    /**
     * @function removeFile
     * @since 1.0.0
     * @hidden
     */
    function removeFile() {
        setFile(null);
    }
    const className = classNames('import-button', {
        'import-button--has-error': error
    });
    const variantDataTypes = ['OPTION_ONLY', 'VARIANT_OPTIONS'];
    const dataTypeOptions = [
        { label: t('Products'), value: 'ITEM_ONLY' },
        { label: t('Variants'), value: 'OPTION_ONLY' },
        { label: t('Product Options'), value: 'ITEM_OPTIONS' },
        { label: t('Variant Options'), value: 'VARIANT_OPTIONS' },
        { label: t('Option Relations'), value: 'ITEM_RELATIONS' },
        {
            label: t('Product Option Relations'),
            value: 'ITEM_OPTION_RELATIONS'
        },
        {
            label: t('Variant Option Relations'),
            value: 'VARIANT_OPTION_RELATIONS'
        }
    ].filter((option) => canManageVariants || !variantDataTypes.includes(option.value));
    const footerActions = (React.createElement(Fragment, null,
        React.createElement(Button, { label: t('Cancel'), onPress: onClose, noPadding: true }),
        React.createElement("div", { className: "modal-import-button-container" },
            React.createElement(Button, { form: "import-form", picto: "upload", style: "primary", label: t('Import'), disabled: !file || importProcessing, processing: importProcessing, submit: true }))));
    return (React.createElement("div", { className: "modal-import" },
        React.createElement(Modal, { picto: "upload", title: t('Import'), onClose: onClose, showFooterAction: true, footerActions: footerActions },
            React.createElement("div", { className: "modal-import-content" },
                React.createElement("div", { className: "modal-import-download-template" },
                    React.createElement("span", { className: "modal-import-download-template-title" }, t('First import? Use this template file.')),
                    React.createElement("span", { className: "modal-import-download-template-description" }, t('Open this file in your spreadsheet tool, add your data, save as CSV and upload it.')),
                    React.createElement(MutationForm, { name: "export-form", mutation: templateMutation, onProcess: onDownloadTemplateSubmit, onSuccess: onDownloadTemplateSuccess, onFailure: onDownloadTemplateFailure },
                        React.createElement(FormSelect, { name: "separator", label: t('Column Separator'), placeholder: t('Select Column Separator'), options: [
                                { label: t('Coma'), value: ',' },
                                { label: t('Semicolon'), value: ';' }
                            ], required: true }),
                        React.createElement(Button, { picto: "download", style: "primary", label: t('Download Template'), disabled: downloadTemplateProcessing, processing: downloadTemplateProcessing, submit: true }))),
                React.createElement("div", { className: "modal-import-drop-file-container" },
                    React.createElement("div", { className: "modal-import-drop-file", onDrop: onDropFile, onDragOver: onDragOver },
                        React.createElement(Icon, { value: !file ? 'fad file-arrow-up' : 'fad file-csv', className: "modal-import-drop-file-picto" }),
                        !file ? (React.createElement(React.Fragment, null,
                            React.createElement("div", null,
                                React.createElement("span", { className: "modal-import-drop-file-title" }, t('Drop your file here, or')),
                                React.createElement("input", { type: "file", id: "file", ref: inputFile, style: { display: 'none' }, onChange: onSelectFile }),
                                React.createElement("div", { onClick: onBrowseClick }, t('browse'))),
                            React.createElement("span", { className: "modal-import-drop-file-description" }, t('Support: .csv')))) : (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: "modal-import-drop-file-selected" },
                                React.createElement("span", { className: "modal-import-drop-file-title" }, file.name),
                                React.createElement(Button, { picto: "fad times-circle", className: "modal-import-drop-file-close-picto", onPress: removeFile }))))),
                    React.createElement(MutationForm, { id: "import-form", name: "import-form", mutation: importMutation, onProcess: onModalSubmit, onSuccess: onSuccess, onFailure: onFailure },
                        isSaleItem && (React.createElement(FormSelect, { name: "optionConfiguration", label: t('Data Type'), placeholder: t('Select the data type to import'), options: dataTypeOptions, onChange: onOptionConfigurationChange, required: true })),
                        React.createElement(FormSelect, { name: "separator", label: t('Column Separator'), placeholder: t('Select Column Separator'), options: [
                                { label: t('Coma'), value: ',' },
                                { label: t('Semicolon'), value: ';' }
                            ], required: true }),
                        optionOnly && (React.createElement(FormSwitch, { name: "applyPriceUpdate", label: t('Apply price update to mapped products?'), infos: t('Do you want to update the price of this option in all products where it is mapped?') }))))))));
}
